// A tab opened before a deploy keeps the OLD build: its lazy pages (dashboard, settings, knowledge…) point at chunk
// files the new deploy no longer has. The static site answers a missing file with index.html, the dynamic import
// fails, and vue-router drops the navigation without a word — the rail button shows its tooltip but "does nothing"
// (owner 26 ก.ย. 69: "กดเมนุข้างๆไม่ได้"). Two fixes:
//   1. a failed page chunk → load the target URL for real (once; no reload loop)
//   2. a newer build is live → the next page change is a real page load (seamless) — unless something typed would
//      be lost (setReloadGuard)
//   3. (owner 28 ก.ย. 69: a chat tab opened before the 04:24 deploy showed only "ตรวจสลิป" under a menu picture — the
//      inbox never changes page, so 2 never fired) a newer build → `newBuild` is set and NewVersionBar.vue shows
//      "มีระบบเวอร์ชันใหม่ · โหลดหน้าใหม่". Never reloads by itself. How we know: vite.config.ts bakes the build id in
//      (__BUILD_ID__) and writes dist/version.json; fetched with no-store + a query (DO static sites answer
//      `cache-control: public,max-age=10,s-maxage=86400` behind their CDN; a deploy purges it, the query makes sure).
//      An older deploy without version.json answers index.html (catch-all) → the entry-script comparison below.
import { ref } from 'vue'
import type { Router } from 'vue-router'

const RELOAD_KEY = 'cf-chat.reload-at'
const LOOP_GUARD_MS = 10_000
const CHECK_EVERY_MS = 5 * 60 * 1000
const MIN_GAP_MS = 20_000 // focus + visibilitychange come together: one check

/** The deployed build when it is not this tab's (NewVersionBar.vue shows the bar). */
export const newBuild = ref<string | null>(null)

let reloadGuard: () => boolean = () => true
/** App.vue: false while something typed would be lost (then a page change stays in the app; the bar stays). */
export function setReloadGuard(fn: () => boolean): void {
  reloadGuard = fn
}

/** `{"build": "…"}` of /version.json, or null (an older deploy answers index.html for it). */
export function parseBuild(body: string, contentType: string | null): string | null {
  if (contentType && !/json/i.test(contentType)) return null
  try {
    const o = JSON.parse(body) as { build?: unknown }
    return typeof o?.build === 'string' && o.build.trim() ? o.build.trim() : null
  } catch {
    return null
  }
}

/**
 * The deployed build vs this tab's: 'new' | 'same' | null (unknown: offline, dev server, an older deploy). Checks
 * /version.json first, then the hashed entry script of index.html.
 */
export async function liveBuild(
  mine: { build: string | null; entry: string | null },
  fetchImpl: typeof fetch = (...a) => fetch(...a),
  now: number = Date.now(),
): Promise<{ state: 'new' | 'same' | null; build: string | null }> {
  try {
    const r = await fetchImpl(`/version.json?t=${now}`, { cache: 'no-store', headers: { accept: 'application/json' } })
    const live = r.ok ? parseBuild(await r.text(), r.headers.get('content-type')) : null
    if (live && mine.build) return { state: live === mine.build ? 'same' : 'new', build: live }
    if (!mine.entry) return { state: null, build: null }
    const h = await fetchImpl('/', { cache: 'no-store', headers: { accept: 'text/html' } })
    if (!h.ok) return { state: null, build: null }
    const entry = entryScript(await h.text())
    if (!entry) return { state: null, build: null }
    return { state: entry === mine.entry ? 'same' : 'new', build: entry }
  } catch {
    return { state: null, build: null } // offline for a moment: try again later
  }
}

const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch|MIME type|Unable to preload CSS|ChunkLoadError/i

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err ?? '')
  return CHUNK_ERROR.test(msg)
}

/** The hashed entry script of an index.html ("assets/index-CYCJRzHO.js"), or null. */
export function entryScript(html: string): string | null {
  const m = /src="([^"]*assets\/index-[^"]+\.js)"/.exec(html)
  return m ? m[1]!.replace(/^.*?(assets\/)/, '$1') : null
}

function currentEntry(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="assets/index-"]')
  return s ? s.getAttribute('src')!.replace(/^.*?(assets\/)/, '$1') : null
}

/** Full page load of `path`, at most once per LOOP_GUARD_MS (a broken deploy must not reload forever). */
export function hardNavigate(path: string, now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
    if (now - last < LOOP_GUARD_MS) return false
    sessionStorage.setItem(RELOAD_KEY, String(now))
  } catch {
    /* no storage: reload anyway */
  }
  window.location.assign(path)
  return true
}

/** '/' and '/c/:id' are the same page (the inbox). */
function page(name: unknown): string {
  return name === 'conversation' ? 'inbox' : String(name ?? '')
}

export function installStaleBuildGuard(router: Router): void {
  let stale = false
  let pending: string | null = null // the page being opened (a failed chunk means: open THAT page for real)
  const mine = currentEntry()

  router.onError((err, to) => {
    if (isChunkLoadError(err)) hardNavigate(to.fullPath)
  })
  // Vite's own signal when a preloaded chunk of the old build is gone
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault()
    hardNavigate(pending ?? window.location.pathname + window.location.search)
  })

  // a newer build → the next navigation loads the page for real (dev has no hashed entry: nothing to compare)
  router.afterEach(() => {
    pending = null
  })
  router.beforeEach((to, from) => {
    pending = to.fullPath
    // only when changing PAGE (not chat → chat inside the inbox: a draft being typed must not be lost)
    if (stale && from.matched.length && page(to.name) !== page(from.name) && reloadGuard() && hardNavigate(to.fullPath)) return false
    return true
  })
  const me = { build: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : null, entry: mine }
  if (!me.entry) return // dev server: no hashed build to compare
  let last = 0
  const check = async (): Promise<void> => {
    const now = Date.now()
    if (now - last < MIN_GAP_MS) return
    last = now
    const r = await liveBuild(me, undefined, now)
    if (r.state === 'new') {
      stale = true
      newBuild.value = r.build
    }
  }
  window.setInterval(() => void check(), CHECK_EVERY_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check()
  })
  window.addEventListener('focus', () => void check())
}

/** What a reload now would lose, in words for the bar ('' = nothing). Drafts in the typing box are not kept. */
export function unsavedText(w: { texts: number; orders: number; reading: boolean }): string {
  const parts: string[] = []
  if (w.texts > 0) parts.push(`ข้อความที่พิมพ์ค้าง ${w.texts} แชท`)
  if (w.orders > 0) parts.push(`ออเดอร์ที่กรอกค้าง ${w.orders} แชท`)
  if (w.reading) parts.push('รายการอ่านเมนูที่ยังไม่ยืนยัน')
  return parts.join(' · ')
}
